import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Section, Card, CardHeader, Pill, LoadingBar } from "../components/Primitives";

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Observatory() {
  const { activeClient, activeClientId } = useClient();
  const [inventory, setInventory] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeClientId) return;
    setLoading(true);
    const params = { params: { client_id: activeClientId } };
    Promise.all([
      api.get("/observatory/automation-inventory", params),
      api.get("/observatory/teams", params),
    ]).then(([a, b]) => {
      setInventory(a.data); setTeams(b.data); setLoading(false);
    });
  }, [activeClientId]);

  if (loading) return <LoadingBar label="Aggregating QA estate" />;

  return (
    <div data-testid="observatory-page">
      <PageHeader
        overline={`Layer 1 · ${activeClient?.name || ""}`}
        title="QA Observatory"
        description="Unified inventory, execution analytics, and maturity scoring across the QA estate."
      />

      <Section>
        <Card testid="automation-inventory-card">
          <CardHeader overline="Automation inventory" title="Test Assets Across the Estate" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead>
                <tr className="text-left">
                  {["Asset", "Framework", "Owner", "Tests", "Pass rate", "Flaky", "Coverage", "Last run"].map((h) => (
                    <th key={h} className="px-6 py-3 overline text-zinc-500 border-b border-zinc-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inventory.map((row) => (
                  <tr key={row.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-3.5 text-zinc-900 font-medium">{row.asset}</td>
                    <td className="px-6 py-3.5 text-zinc-600 font-mono text-xs">{row.framework}</td>
                    <td className="px-6 py-3.5 text-zinc-600">{row.owner}</td>
                    <td className="px-6 py-3.5 font-mono text-zinc-900">{row.tests}</td>
                    <td className="px-6 py-3.5 font-mono">
                      <span className={row.pass_rate >= 95 ? "text-emerald-700" : row.pass_rate >= 88 ? "text-zinc-900" : "text-amber-700"}>
                        {row.pass_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-3.5 font-mono text-zinc-700">{row.flaky}</td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1 bg-zinc-100">
                          <div className="h-full bg-vlink-500" style={{ width: `${row.coverage}%` }} />
                        </div>
                        <span className="font-mono text-xs text-zinc-700">{row.coverage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-zinc-500 text-xs font-mono">{fmtTime(row.last_run)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card testid="teams-card">
            <CardHeader overline="QA maturity & SME dependency" title="Team Posture" />
            <div className="px-6 py-4 space-y-4">
              {teams.map((t) => (
                <div key={t.id} className="border-b border-zinc-100 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-medium text-zinc-900">{t.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{t.headcount} ppl · {t.maturity}</div>
                    </div>
                    <Pill tone={t.maturity_score >= 4 ? "success" : t.maturity_score >= 3 ? "neutral" : "warn"}>
                      {t.maturity_score.toFixed(1)} / 5
                    </Pill>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="overline text-zinc-500 mb-1">Automation</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1 bg-zinc-100"><div className="h-full bg-vlink-500" style={{ width: `${t.automation_pct}%` }} /></div>
                        <span className="font-mono text-zinc-700">{t.automation_pct}%</span>
                      </div>
                    </div>
                    <div>
                      <div className="overline text-zinc-500 mb-1">SME Dependency</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1 bg-zinc-100"><div className={`h-full ${t.sme_dependency >= 70 ? "bg-rose-600" : "bg-amber-500"}`} style={{ width: `${t.sme_dependency}%` }} /></div>
                        <span className="font-mono text-zinc-700">{t.sme_dependency}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card testid="estate-summary-card">
            <CardHeader overline="Aggregate" title="Estate Summary" />
            <div className="p-6 grid grid-cols-2 gap-6">
              <div>
                <div className="overline text-zinc-500">Total assets</div>
                <div className="metric-value text-4xl text-zinc-900 mt-1">{inventory.length}</div>
              </div>
              <div>
                <div className="overline text-zinc-500">Total tests</div>
                <div className="metric-value text-4xl text-zinc-900 mt-1">
                  {inventory.reduce((s, i) => s + i.tests, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="overline text-zinc-500">Avg pass rate</div>
                <div className="metric-value text-4xl text-zinc-900 mt-1">
                  {(inventory.reduce((s, i) => s + i.pass_rate, 0) / Math.max(1, inventory.length)).toFixed(1)}<span className="text-zinc-400 text-base font-mono ml-1">%</span>
                </div>
              </div>
              <div>
                <div className="overline text-zinc-500">Flaky tests</div>
                <div className="metric-value text-4xl text-rose-700 mt-1">
                  {inventory.reduce((s, i) => s + i.flaky, 0)}
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 mt-2">
              <div className="overline text-zinc-500 mb-2">Frameworks in use</div>
              <div className="flex flex-wrap gap-2">
                {[...new Set(inventory.map((i) => i.framework))].map((f) => (
                  <Pill key={f} tone="neutral">{f}</Pill>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </Section>
    </div>
  );
}
